import { inject, Injectable } from '@angular/core';
import { ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import { Observable, map, of, tap } from 'rxjs';
import {
  DsmDealProduct,
  DsmKitProductLocationsGroup,
  DsmModelElevationProductApiModel,
  DsmProductAttributeGroup,
  DsmProductAttributesResponse,
  DsmProductFilters,
  DsmProductOption,
  ProjectModelRoomQuantityLookup
} from '../models/dsm-deal-options.models';
import { LocationItem } from '@zayan-erp/home-building';

export interface DsmLocationScope {
  addendumId: string | null;
  lotId: string | null;
}

@Injectable({ providedIn: 'root' })
export class DsmModelElevationProductsService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  /** `showLoader` is opt-out for screens that render their own inline loading state.
   *  Pass `buttonId` with showLoader to use a button spinner instead of the full-screen overlay. */
  getModelElevationProducts(
    modelId: string,
    elevationId: string,
    filters?: DsmProductFilters,
    showLoader = true,
    buttonId?: string
  ): Observable<DsmProductOption[]> {
    const endpoint = this.#buildEndpoint(modelId, elevationId, filters);

    return this.#api
      .getData<DsmModelElevationProductApiModel[]>(`${this.baseUrl}/modelelevationproducts/${endpoint}`, {
        showLoader,
        showMessage: false,
        showError: !buttonId,
        buttonId
      })
      .pipe(map(products => products.map((api: DsmModelElevationProductApiModel) => this.mapApiToProduct(api))));
  }

  #buildEndpoint(modelId: string, elevationId: string, filters?: DsmProductFilters): string {
    const queryParams = this.#buildQueryParams(filters);
    return `models/${modelId}/elevations/${elevationId}${queryParams ? `?${queryParams}` : ''}`;
  }

  #buildQueryParams(filters?: DsmProductFilters): string {
    if (!filters) return '';

    const params = new URLSearchParams();
    const paramMap: Record<string, string | boolean | null | undefined> = {
      LotId: filters.lotId,
      RoomId: filters.roomId,
      CategoryId: filters.categoryId,
      SubcategoryId: filters.subcategoryId,
      OptionTypeId: filters.optionTypeId,
      CatalogChannel: '1'
    };

    Object.entries(paramMap).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    });

    if (filters.applyCutoff === true) {
      params.set('ApplyCutoff', 'true');
    }

    return params.toString();
  }

  mapApiToProduct(api: DsmModelElevationProductApiModel): DsmProductOption {
    const DEFAULT_VALUE = '—';

    return {
      modelElevationProductsId: api.id,
      productId: api.product?.id ?? api.productId,
      code: api.product?.code ?? DEFAULT_VALUE,
      description: api.product?.description ?? DEFAULT_VALUE,
      salesDescription: api.descriptionOverride
        ? (api.salesDescription ?? DEFAULT_VALUE)
        : (api.product?.salesDescription ?? DEFAULT_VALUE),
      type: api.productType?.name ?? api.product?.productClass?.name ?? DEFAULT_VALUE,
      uom: api.product?.uofm?.code ?? api.product?.uofm?.description ?? DEFAULT_VALUE,
      quantity: api.quantity ?? 0,
      uofmId: api.product?.uofm?.id ?? '',
      unitPrice: api.unitPrice ?? 0,
      selectionRequired: api.product?.selectionRequired ?? false,
      category: api.product?.subcategory?.category?.code ?? DEFAULT_VALUE,
      subcategory: api.product?.subcategory?.code ?? DEFAULT_VALUE,
      images: api.product?.images ?? [],
      isKit: api.product?.productClass?.name === 'Kit',
      linkedProducts: (api.linkedProducts ?? []).map(child => this.mapApiToProduct(child))
    };
  }

  getProductAttributes(productId: string, groupByAttribute = true): Observable<DsmProductAttributeGroup[]> {
    const query = groupByAttribute ? '?groupByAttribute=true' : '';
    return this.#api.getData<DsmProductAttributeGroup[]>(`${this.baseUrl}/products/${productId}/attributes${query}`, {
      showLoader: false,
      showMessage: false,
      showError: false
    });
  }

  getProductAttributesGrouped(productId: string): Observable<DsmProductAttributesResponse> {
    return this.#api.getData<DsmProductAttributesResponse>(
      `${this.baseUrl}/products/${productId}/attributes?groupByAttribute=true`,
      { showLoader: false, showMessage: false }
    );
  }

  getProductLocations(addendumId: string, productId: string): Observable<LocationItem[]> {
    return this.#api.getData<LocationItem[]>(
      `${this.baseUrl}/addendums/${addendumId}/locations?productId=${productId}`,
      {
        showLoader: false,
        showMessage: false
      }
    );
  }

  getKitProductLocations(addendumId: string, productId: string): Observable<DsmKitProductLocationsGroup[]> {
    return this.#api.getData<DsmKitProductLocationsGroup[]>(
      `${this.baseUrl}/addendums/${addendumId}/kit-product-locations?productId=${productId}`,
      {
        showLoader: false,
        showMessage: false
      }
    );
  }

  // Lot-scoped twins of the two addendum reads above, for the appointment screens where no addendum exists yet.
  getLotProductLocations(lotId: string, productId: string): Observable<LocationItem[]> {
    return this.#api.getData<LocationItem[]>(
      `${this.baseUrl}/addendums/lots/${lotId}/locations?productId=${productId}`,
      {
        showLoader: false,
        showMessage: false
      }
    );
  }

  getLotKitProductLocations(lotId: string, productId: string): Observable<DsmKitProductLocationsGroup[]> {
    return this.#api.getData<DsmKitProductLocationsGroup[]>(
      `${this.baseUrl}/addendums/lots/${lotId}/kit-product-locations?productId=${productId}`,
      {
        showLoader: false,
        showMessage: false
      }
    );
  }

  /** The addendum wins when there is one (APS screens); otherwise the lot (appointment screens); otherwise none. */
  getScopedProductLocations(scope: DsmLocationScope, productId: string): Observable<LocationItem[]> {
    if (scope.addendumId) return this.getProductLocations(scope.addendumId, productId);
    if (scope.lotId) return this.getLotProductLocations(scope.lotId, productId);
    return of([]);
  }

  getScopedKitProductLocations(scope: DsmLocationScope, productId: string): Observable<DsmKitProductLocationsGroup[]> {
    if (scope.addendumId) return this.getKitProductLocations(scope.addendumId, productId);
    if (scope.lotId) return this.getLotKitProductLocations(scope.lotId, productId);
    return of([]);
  }

  getModelElevationProductById(modelElevationProductId: string): Observable<DsmModelElevationProductApiModel | null> {
    return this.#api
      .getData<DsmModelElevationProductApiModel>(`${this.baseUrl}/modelelevationproducts/${modelElevationProductId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(map(product => product ?? null));
  }

  getAddendumOptionDetailsById(modelElevationProductId: string): Observable<DsmDealProduct | null> {
    return this.#api
      .getData<DsmDealProduct>(`${this.baseUrl}/addendum-products/${modelElevationProductId}`, {
        showLoader: true,
        showMessage: false
      })
      .pipe(map(product => product ?? null));
  }

  getRoomQuantityLookup(
    addendumId: string,
    productId: string,
    locationId: string,
    uofmId: string
  ): Observable<ProjectModelRoomQuantityLookup> {
    const params = new URLSearchParams({ addendumId, productId, locationId, uofmId });
    return this.#api.getData<ProjectModelRoomQuantityLookup>(
      `${this.baseUrl}/project/model/rooms/quantity?${params.toString()}`,
      { showLoader: false, showMessage: false }
    );
  }
}
